import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import AdminShell from '../../components/admin/AdminShell';
import { adminV2Api } from '../../lib/api';
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, Ticket, User, Calendar,
  MapPin, Clock, Wallet, CreditCard, AlertTriangle, RefreshCcw, Minus, Plus,
  Crown, Hash, ExternalLink, Sparkles, ShieldCheck, ShieldX, Bug,
} from 'lucide-react';

const fmtINR = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  if (isNaN(h)) return hhmm;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};
const fmtDateTime = (ts) => ts ? new Date(ts).toLocaleString('en-IN') : '';

const STATUS_COLORS = {
  pending:            'bg-amber-500/15 text-amber-300 border-amber-400/40',
  awaiting_approval:  'bg-purple-500/15 text-purple-300 border-purple-400/40',
  confirmed:          'bg-emerald-500/15 text-emerald-300 border-emerald-400/40',
  cancelled:          'bg-red-500/15 text-red-300 border-red-400/40',
  refunded:           'bg-red-500/15 text-red-300 border-red-400/40',
  partially_refunded: 'bg-amber-500/15 text-amber-300 border-amber-400/40',
};

// ─── Refund Panel ─────────────────────────────────────────────────────────
const RefundPanel = ({ booking, onRefunded }) => {
  const [mode, setMode] = useState('partial');  // partial | full
  const [qtys, setQtys] = useState({});         // {pass_type_id: qty}
  const [refundToWallet, setRefundToWallet] = useState(true);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const remainingByPass = (item) => Math.max(0, (item.quantity || 0) - (item.refunded_qty || 0));
  const items = (booking.items || []).filter((i) => remainingByPass(i) > 0);

  const refundTotal = mode === 'full'
    ? items.reduce((s, i) => s + i.price_at_purchase * remainingByPass(i), 0)
    : items.reduce((s, i) => s + (qtys[i.pass_type_id] || 0) * i.price_at_purchase, 0);

  const canSubmit = refundTotal > 0 && !submitting;

  const submit = async () => {
    setSubmitting(true);
    try {
      let payload = { refund_to_wallet: refundToWallet, reason: reason || 'admin_refund' };
      if (mode === 'partial') {
        payload.items = items
          .filter((i) => (qtys[i.pass_type_id] || 0) > 0)
          .map((i) => ({ pass_type_id: i.pass_type_id, quantity: qtys[i.pass_type_id] }));
        if (payload.items.length === 0) {
          toast.error('Select at least one pass to refund');
          setSubmitting(false);
          return;
        }
      }
      // full refund: items undefined → backend refunds all remaining
      await adminV2Api.refund(booking._id, payload);
      toast.success(mode === 'full' ? 'Full refund processed' : 'Partial refund processed');
      onRefunded();
      setQtys({}); setConfirming(false); setReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Refund failed');
    } finally { setSubmitting(false); }
  };

  if (items.length === 0) {
    return (
      <div className="booking-detail-card">
        <h3 className="booking-detail-card-title">Refunds</h3>
        <p className="text-sm text-zinc-500 mt-2">All items have already been refunded.</p>
      </div>
    );
  }

  return (
    <div className="booking-detail-card" data-testid="refund-panel">
      <header className="flex items-center justify-between">
        <h3 className="booking-detail-card-title flex items-center gap-2">
          <RefreshCcw size={14} className="text-[var(--accent-gold)]" />
          Refunds
        </h3>
        <div className="refund-mode-toggle">
          <button
            type="button"
            onClick={() => setMode('partial')}
            className={mode === 'partial' ? 'active' : ''}
            data-testid="refund-mode-partial"
          >Partial</button>
          <button
            type="button"
            onClick={() => { setMode('full'); setQtys({}); }}
            className={mode === 'full' ? 'active' : ''}
            data-testid="refund-mode-full"
          >Full</button>
        </div>
      </header>

      {mode === 'partial' && (
        <div className="refund-items mt-4">
          {items.map((it) => {
            const remaining = remainingByPass(it);
            const q = qtys[it.pass_type_id] || 0;
            return (
              <div key={it.pass_type_id} className="refund-item-row" data-testid={`refund-item-${it.pass_type_name}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{it.pass_type_name}</div>
                  <div className="text-[11px] text-zinc-500">
                    {fmtINR(it.price_at_purchase)} each · {remaining} refundable
                  </div>
                </div>
                <div className="refund-stepper">
                  <button type="button" onClick={() => setQtys({ ...qtys, [it.pass_type_id]: Math.max(0, q - 1) })} disabled={q <= 0}>
                    <Minus size={12} />
                  </button>
                  <span className="refund-qty">{q}</span>
                  <button type="button" onClick={() => setQtys({ ...qtys, [it.pass_type_id]: Math.min(remaining, q + 1) })} disabled={q >= remaining}>
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="refund-summary mt-4">
        <div className="text-sm text-zinc-400">Refund total</div>
        <div className="text-xl font-bold text-white font-mono" data-testid="refund-total">{fmtINR(refundTotal)}</div>
      </div>

      <div className="refund-options mt-4">
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={refundToWallet}
            onChange={(e) => setRefundToWallet(e.target.checked)}
            className="editor-checkbox"
            data-testid="refund-to-wallet"
          />
          <span className="text-zinc-300">Credit refund to customer’s wallet</span>
        </label>
        <p className="text-[11px] text-zinc-500 mt-1 ml-6">
          Recommended. Non-expiring loyalty credit. Uncheck to mark refund-to-gateway (mock).
        </p>
      </div>

      <input
        type="text"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (optional)"
        className="editor-input mt-3"
        data-testid="refund-reason"
      />

      {!confirming ? (
        <button
          type="button"
          className="btn-danger w-full mt-4"
          onClick={() => setConfirming(true)}
          disabled={!canSubmit}
          data-testid="refund-process-btn"
        >
          <RefreshCcw size={14} />
          Process {mode === 'full' ? 'Full' : 'Partial'} Refund
        </button>
      ) : (
        <div className="refund-confirm mt-4">
          <AlertTriangle size={16} className="text-amber-400 shrink-0" />
          <div className="flex-1 text-sm text-zinc-300">
            Refund <strong className="text-white font-mono">{fmtINR(refundTotal)}</strong>?
            Inventory will be restored.
          </div>
          <button onClick={() => setConfirming(false)} className="btn-gold-outline text-xs px-3 py-1.5">Cancel</button>
          <button onClick={submit} disabled={submitting} className="btn-danger text-xs px-3 py-1.5" data-testid="refund-confirm-btn">
            {submitting ? <Loader2 size={12} className="animate-spin" /> : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
};


// ─── Main page ───────────────────────────────────────────────────────────────
export default function AdminBookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vipBusy, setVipBusy] = useState(false);

  const fetchBooking = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await adminV2Api.getBooking(id);
      setBooking(res.data);
    } catch (e) {
      setError(e?.response?.status === 404 ? 'Booking not found' : 'Could not load booking');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  const decideVip = async (approve) => {
    setVipBusy(true);
    try {
      await adminV2Api.vipDecision(id, approve);
      toast.success(approve ? 'VIP booking approved' : 'VIP booking rejected & refunded');
      await fetchBooking();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || 'Action failed');
    } finally { setVipBusy(false); }
  };

  if (loading) {
    return (
      <AdminShell title="Booking">
        <div className="events-loading"><Loader2 size={26} className="animate-spin text-[var(--accent-gold)]" /><p>Loading…</p></div>
      </AdminShell>
    );
  }

  if (error || !booking) {
    return (
      <AdminShell title="Booking">
        <div className="events-error"><Ticket size={28} className="text-zinc-500 mb-2" /><p>{error}</p><Link to="/admin/bookings" className="btn-gold mt-3">All Bookings</Link></div>
      </AdminShell>
    );
  }

  const isPaid = booking.payment_status === 'paid';
  const canRefund = isPaid && ['confirmed', 'awaiting_approval', 'partially_refunded'].includes(booking.booking_status);
  const totalPasses = (booking.items || []).reduce((s, i) => s + i.quantity, 0);
  const refundedSoFar = (booking.refunds || []).reduce((s, r) => s + (r.total_amount || 0), 0);

  return (
    <AdminShell title={`Booking ${booking.booking_code}`} subtitle={booking.event_title}>
      <div className="booking-detail-page" data-testid="booking-detail-page">
        <Link to="/admin/bookings" className="event-back-btn mb-4 inline-flex" data-testid="booking-back">
          <ArrowLeft size={14} /> All bookings
        </Link>

        <div className="booking-detail-grid">
          {/* LEFT: Booking summary */}
          <div className="space-y-4">
            <div className="booking-detail-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="booking-detail-event-title">{booking.event_title}</h2>
                  <div className="booking-detail-event-meta">
                    <span><Calendar size={11} className="text-[var(--accent-gold)]" /> {fmtDate(booking.event_date)}</span>
                    {booking.event_time && <span><Clock size={11} className="text-[var(--accent-gold)]" /> {fmtTime(booking.event_time)}</span>}
                    {booking.venue && <span><MapPin size={11} className="text-[var(--accent-gold)]" /> {booking.venue}</span>}
                  </div>
                </div>
                <span className={`status-pill ${STATUS_COLORS[booking.booking_status] || ''}`} data-testid="booking-status-pill">
                  {booking.booking_status?.replace('_', ' ').toUpperCase()}
                </span>
              </div>

              <div className="booking-detail-meta mt-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Booking code</div>
                  <div className="text-sm font-mono text-white mt-0.5" data-testid="booking-code">{booking.booking_code}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Customer ID</div>
                  <div className="text-[11px] font-mono text-zinc-400 mt-0.5 truncate">{booking.user_id}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Created</div>
                  <div className="text-xs text-zinc-300 mt-0.5">{fmtDateTime(booking.created_at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Paid at</div>
                  <div className="text-xs text-zinc-300 mt-0.5">{booking.paid_at ? fmtDateTime(booking.paid_at) : '—'}</div>
                </div>
              </div>
            </div>

            {/* Pass breakdown */}
            <div className="booking-detail-card" data-testid="booking-passes">
              <h3 className="booking-detail-card-title flex items-center gap-2">
                <Ticket size={14} className="text-[var(--accent-gold)]" /> Passes ({totalPasses})
              </h3>
              <ul className="booking-detail-items mt-3">
                {(booking.items || []).map((it, idx) => {
                  const refunded = it.refunded_qty || 0;
                  const remaining = it.quantity - refunded;
                  return (
                    <li key={idx} className="booking-detail-item" data-testid={`booking-item-${idx}`}>
                      <div>
                        <span className="text-sm text-zinc-200 font-medium">{it.pass_type_name}</span>
                        {it.is_vip && <Crown size={11} className="text-[var(--accent-gold)] ml-1.5 inline" />}
                        <div className="text-[11px] text-zinc-500 mt-0.5">
                          {fmtINR(it.price_at_purchase)} each
                          {refunded > 0 && <span className="text-amber-400 ml-2">· {refunded} refunded</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-zinc-200 font-mono">× {it.quantity}</div>
                        {refunded > 0 && <div className="text-[10px] text-zinc-500">({remaining} active)</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Payment breakdown */}
            <div className="booking-detail-card" data-testid="booking-payment">
              <h3 className="booking-detail-card-title flex items-center gap-2">
                <CreditCard size={14} className="text-[var(--accent-gold)]" /> Payment
              </h3>
              <div className="booking-detail-totals mt-3">
                <div className="row"><span>Subtotal</span><span className="font-mono">{fmtINR(booking.total_amount)}</span></div>
                {booking.wallet_amount > 0 && (
                  <div className="row"><span className="text-emerald-400 inline-flex items-center gap-1"><Wallet size={11} /> Wallet</span><span className="font-mono text-emerald-400">− {fmtINR(booking.wallet_amount)}</span></div>
                )}
                <div className="row"><span>Gateway</span><span className="font-mono">{fmtINR(booking.gateway_amount)}</span></div>
                <div className="row row-total">
                  <span>Paid</span>
                  <span className="font-mono" data-testid="booking-paid-amount">{fmtINR(booking.paid_amount)}</span>
                </div>
                {refundedSoFar > 0 && (
                  <div className="row text-amber-400"><span>Refunded so far</span><span className="font-mono">{fmtINR(refundedSoFar)}</span></div>
                )}
              </div>
            </div>

            {/* Check-in / scan history */}
            {(booking.checked_in || booking.public_ticket_token) && (
              <div className="booking-detail-card" data-testid="booking-checkin">
                <h3 className="booking-detail-card-title flex items-center gap-2">
                  <Sparkles size={14} className="text-[var(--accent-gold)]" /> Ticket & Check-in
                </h3>
                <div className="mt-3 space-y-2 text-sm">
                  {booking.public_ticket_token && (
                    <a
                      href={`/tickets/${booking.public_ticket_token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="booking-ticket-link"
                      data-testid="booking-public-ticket-link"
                    >
                      <ExternalLink size={12} /> Open public ticket page
                    </a>
                  )}
                  {booking.checked_in ? (
                    <div className="booking-checkin-row">
                      <CheckCircle2 size={14} className="text-emerald-400" />
                      <span className="text-zinc-300">Checked in</span>
                      <span className="text-zinc-500 ml-auto text-xs">{fmtDateTime(booking.checked_in_at)}</span>
                    </div>
                  ) : (
                    <div className="booking-checkin-row">
                      <Clock size={14} className="text-amber-400" />
                      <span className="text-zinc-400">Not yet scanned</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Refund history */}
            {(booking.refunds || []).length > 0 && (
              <div className="booking-detail-card" data-testid="booking-refund-history">
                <h3 className="booking-detail-card-title flex items-center gap-2">
                  <RefreshCcw size={14} className="text-amber-400" /> Refund History
                </h3>
                <ul className="booking-detail-refunds mt-3">
                  {booking.refunds.map((r) => (
                    <li key={r._id} className="booking-detail-refund-row">
                      <div>
                        <div className="text-sm text-zinc-200 font-mono">− {fmtINR(r.total_amount)}</div>
                        <div className="text-[11px] text-zinc-500">{r.reason} · {fmtDateTime(r.created_at)}</div>
                      </div>
                      <div className="text-[11px] text-emerald-400">{r.to_wallet ? 'Wallet' : 'Gateway'}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* RIGHT: Actions */}
          <div className="space-y-4">
            {/* VIP approve/reject */}
            {booking.booking_status === 'awaiting_approval' && (
              <div className="booking-detail-card booking-detail-card-vip" data-testid="vip-decision-card">
                <h3 className="booking-detail-card-title flex items-center gap-2">
                  <Crown size={14} className="text-[var(--accent-gold)]" /> VIP Approval
                </h3>
                <p className="text-xs text-zinc-400 mt-2">
                  This booking includes a VIP pass that requires manual approval before the ticket is issued.
                  Rejecting will release inventory and credit the full paid amount to the customer’s wallet.
                </p>
                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => decideVip(true)}
                    disabled={vipBusy}
                    className="vip-approve-btn flex-1"
                    data-testid="vip-approve-btn"
                  >
                    {vipBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => decideVip(false)}
                    disabled={vipBusy}
                    className="vip-reject-btn flex-1"
                    data-testid="vip-reject-btn"
                  >
                    {vipBusy ? <Loader2 size={14} className="animate-spin" /> : <ShieldX size={14} />}
                    Reject & Refund
                  </button>
                </div>
              </div>
            )}

            {/* Refund panel */}
            {canRefund && <RefundPanel booking={booking} onRefunded={fetchBooking} />}

            {/* Paytm gateway response — populated only when booking went through Paytm */}
            {booking.paytm_webhook && <PaytmDebugCard webhook={booking.paytm_webhook} />}

            {/* Quick info card */}
            <div className="booking-detail-card">
              <h3 className="booking-detail-card-title flex items-center gap-2">
                <Hash size={14} className="text-zinc-500" /> Internal
              </h3>
              <div className="text-[11px] text-zinc-500 mt-2 space-y-1.5 font-mono">
                <div>booking_id: <span className="text-zinc-300">{booking._id}</span></div>
                <div>event_id: <span className="text-zinc-300">{booking.event_id}</span></div>
                {booking.gateway_txn_id && <div>txn: <span className="text-zinc-300">{booking.gateway_txn_id}</span></div>}
                {booking.wallet_txn_id && <div>wallet_txn: <span className="text-zinc-300">{booking.wallet_txn_id}</span></div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Paytm gateway response — diagnostic surface for failed transactions
// ──────────────────────────────────────────────────────────────────────
function PaytmDebugCard({ webhook }) {
  const raw = webhook?.raw_callback || {};
  const respCode  = raw.RESPCODE  || webhook.paytm_status || '—';
  const respMsg   = raw.RESPMSG   || '—';
  const txnStatus = raw.STATUS    || webhook.paytm_status || '—';
  const mapped    = webhook.mapped || '—';
  const txnId     = raw.TXNID     || webhook.txn_id || '—';
  const bankTxn   = raw.BANKTXNID || webhook.bank_txn_id || '—';
  const payMode   = raw.PAYMENTMODE || webhook.payment_mode || '—';
  const txnAmt    = raw.TXNAMOUNT || webhook.txn_amount || '—';

  const isFailure = mapped !== 'paid';

  return (
    <div
      className={`booking-detail-card ${isFailure ? 'border border-red-700/40' : 'border border-emerald-700/30'}`}
      data-testid="paytm-debug-card"
    >
      <h3 className="booking-detail-card-title flex items-center gap-2">
        <Bug size={14} className={isFailure ? 'text-red-400' : 'text-emerald-400'} />
        Paytm Gateway Response
        <span
          className={`ml-auto text-[10px] uppercase px-2 py-0.5 rounded-full ${
            isFailure ? 'bg-red-900/30 text-red-300' : 'bg-emerald-900/30 text-emerald-300'
          }`}
        >
          {mapped}
        </span>
      </h3>

      {isFailure && respMsg && respMsg !== '—' && (
        <div className="mt-3 p-2.5 rounded bg-red-900/15 border border-red-700/30">
          <p className="text-[10px] uppercase tracking-wider text-red-300/80 mb-1">Failure reason</p>
          <p className="text-sm text-red-200 font-mono break-words" data-testid="paytm-respmsg">
            {respMsg}
          </p>
        </div>
      )}

      <div className="text-[11px] text-zinc-400 mt-3 space-y-1.5 font-mono">
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">STATUS</span>
          <span className="col-span-2 text-zinc-200 break-all">{txnStatus}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">RESPCODE</span>
          <span className="col-span-2 text-zinc-200 break-all">{respCode}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">RESPMSG</span>
          <span className="col-span-2 text-zinc-200 break-all">{respMsg}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">TXNID</span>
          <span className="col-span-2 text-zinc-200 break-all">{txnId}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">BANKTXNID</span>
          <span className="col-span-2 text-zinc-200 break-all">{bankTxn}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">PAYMENTMODE</span>
          <span className="col-span-2 text-zinc-200 break-all">{payMode}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <span className="text-zinc-500">TXNAMOUNT</span>
          <span className="col-span-2 text-zinc-200 break-all">{txnAmt}</span>
        </div>
      </div>

      <details className="mt-3">
        <summary className="text-[10px] text-zinc-500 cursor-pointer hover:text-zinc-300">
          Full raw_callback (debug)
        </summary>
        <pre className="mt-2 p-2 bg-black/50 rounded text-[10px] text-zinc-400 font-mono overflow-x-auto whitespace-pre-wrap break-words">
          {JSON.stringify(raw, null, 2)}
        </pre>
      </details>
    </div>
  );
}
